import { useState, useRef, useEffect, memo } from "react";
import { FiPaperclip, FiSend } from "react-icons/fi";
import defaultImg from "../../assets/user_default.png";
import { db } from "firebaseConfig";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import { CHAT_TYPES, MESSAGE_TYPES } from "helper/constant";
import { getChatKey, getDateKey } from "helper/appConstant";
import moment from "moment";
import LocationPickerModal from "pages/messages/LocationPickerModal";
import { CiLocationOn } from "react-icons/ci";
import { userImageUpload } from "store/slice/auth";
import { useDispatch } from "react-redux";
import { MdAddLocationAlt } from "react-icons/md";
import { PiDotsThreeCircleLight } from "react-icons/pi";
import DropDown from "component/ui/dropDown";
import ConfirmationDialog from "component/dialogs/ConfirmationDialog";
import FullPageLoader from "component/ui/fullPageLoader";
import GroupDetailsDialog from "pages/messages/groupDetailsDialog";
import { useScreenType } from "component/common/screenType";
import { useLoginData } from "component/common/loginData";

const ChatScreen = ({
  data,
  src,
  activeChatData,
  setMessages,
  handleGetInfo,
  handleOpenChat,
  filteredData,
  setActiveChatData,
  handleOpenEditChat,
  onClose,
}) => {
  const [message, setMessage] = useState("");
  const [showActions, setShowActions] = useState(false);
  const [showLocationPicker, setShowLocationPicker] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [showGroupDetails, setShowGroupDetails] = useState(false);
  const fileInputRef = useRef(null);
  const bottomRef = useRef(null);
  const screenType = useScreenType();
  const dispatch = useDispatch();
  const userData = useLoginData();
  const CURRENT_USER_ID = userData?.id;
  const [loader, setLoader] = useState(false);
  // 🔹 Real-time messages
  useEffect(() => {
    if (!activeChatData?.id) return;

    const q = query(
      collection(
        db,
        process.env.REACT_APP_DB_NAME,
        activeChatData?.id,
        "messages"
      ),
      orderBy("createdAt", "asc")
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      setMessages(
        snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }))
      );
    });

    return () => unsubscribe();
  }, [activeChatData, setMessages]);

  // 🔹 Auto scroll to bottom
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    setShowActions(false);
  }, [data]);

  /* ----------------------------------
     GROUP BY DATE
     ---------------------------------- */
  const groupMessagesByDate = (messages = []) => {
    return messages.reduce((acc, msg) => {
      const dateKey = getDateKey(msg?.createdAt);

      if (!acc[dateKey]) {
        acc[dateKey] = [];
      }

      acc[dateKey].push(msg);
      return acc;
    }, {});
  };
  const groupedMessages = groupMessagesByDate(data);

  // handle send message
  const sendMessage = async (
    text = message,
    type = MESSAGE_TYPES?.TEXT,
    position
  ) => {
    // if (type == MESSAGE_TYPES.LOCATION && (!text.trim() || !activeChatData?.id))
    //   return;

    const chatRef = doc(db, process.env.REACT_APP_DB_NAME, activeChatData.id);

    try {
      //Add message to messages subcollection
      await addDoc(
        collection(
          db,
          process.env.REACT_APP_DB_NAME,
          activeChatData.id,
          "messages"
        ),
        {
          senderId: CURRENT_USER_ID,
          text,
          createdAt: serverTimestamp(),
          messageType: type,

          ...(type === MESSAGE_TYPES.LOCATION && {
            location: {
              lat: position?.lat,
              lng: position?.lng,
              address: position?.address ?? "",
            },
          }),
        }
      );

      //Get current chat document to fetch participants & unreadCounts
      const chatSnap = await getDoc(chatRef);
      const chatData = chatSnap.data();

      const unreadCounts = { ...chatData.unreadCounts };

      // Increment unread count for all participants except sender
      chatData.participants.forEach((userId) => {
        if (userId !== CURRENT_USER_ID) {
          unreadCounts[userId] = (unreadCounts[userId] || 0) + 1;
        }
      });

      // Update chat document: lastMessage, lastMessageDate, updatedAt, unreadCounts
      await updateDoc(chatRef, {
        lastMessage:
          type == MESSAGE_TYPES?.LOCATION
            ? "Sent Location"
            : type == MESSAGE_TYPES?.IMAGE
            ? "Image"
            : text,
        lastMessageDate: serverTimestamp(),
        updatedAt: serverTimestamp(),
        unreadCounts,
      });

      // 5️⃣ Clear input
      setMessage("");
    } catch (error) {
      console.error("Error sending message:", error);
    }
  };

  const handleAttachment = () => {
    fileInputRef.current.click();
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files;
    if (!file) return;
    const formData = new FormData();
    if (typeof file === "object" && file !== null) {
      Object.entries(file).forEach(([key, value]) => {
        formData.append("file", value);
      });
    }
    try {
      // Dispatch the upload action
      const data = await dispatch(
        userImageUpload(formData, "chatMedia", "image")
      );
      if (data?.length > 0) {
        data?.map((item) => {
          sendMessage(item?.path, MESSAGE_TYPES?.IMAGE);
        });
      }
    } catch (error) {
      console.error("Error uploading files:", error);
    }
  };

  const ActionItems = ["View", "Edit", "Delete"];

  // delete group function
  const deleteChatMessages = async (chatRoomId) => {
    const messagesRef = collection(
      db,
      process.env.REACT_APP_DB_NAME,
      activeChatData.id,
      "messages"
    );
    const snapshot = await getDocs(messagesRef);

    const deletions = snapshot.docs.map((msg) =>
      deleteDoc(doc(db, messagesRef.path, msg.id))
    );

    await Promise.all(deletions);
  };
  const handleDeleteGroup = async () => {
    setLoader(true);
    setDeleteDialog(false);
    await deleteChatMessages(activeChatData.id);
    await deleteDoc(doc(db, process.env.REACT_APP_DB_NAME, activeChatData.id));
    setLoader(false);
    if (screenType == "desktop") {
      const chatList = filteredData?.filter(
        (item) => item?.id != activeChatData?.id
      );
      if (chatList?.length > 0) {
        setActiveChatData(chatList[0]);
      }
    } else {
      onClose();
    }
  };

  // handle exit group
  const handleLeftGroup = async () => {
    const chatRef = doc(db, process.env.REACT_APP_DB_NAME, activeChatData?.id);
    try {
      const { [CURRENT_USER_ID]: removed, ...rest } = activeChatData.usersInfo;
      await updateDoc(chatRef, {
        participants: activeChatData?.participants?.filter(
          (item) => item != CURRENT_USER_ID
        ),
        usersInfo: rest,
        updatedAt: serverTimestamp(),
      });
      //Add message to messages subcollection
      await addDoc(
        collection(
          db,
          process.env.REACT_APP_DB_NAME,
          activeChatData?.id,
          "messages"
        ),
        {
          senderId: "SYSTEM",
          text: `${userData?.name} left the group.`,
          messageType: "system",
          createdAt: serverTimestamp(),
        }
      );
      setShowGroupDetails(false);
      if (screenType == "desktop") {
        const chatList = filteredData?.filter(
          (item) => item?.id != activeChatData?.id
        );
        if (chatList?.length > 0) {
          setActiveChatData(chatList[0]);
        }
      } else {
        onClose();
      }
    } catch (error) {
      console.error("Error sending message:", error);
    }
  };
  return (
    <div className="flex flex-col h-full bg-gray-100">
      {loader && <FullPageLoader />}
      <LocationPickerModal
        isOpen={showLocationPicker}
        onClose={() => setShowLocationPicker(false)}
        onSelectLocation={(value) => {
          console.log("value", value);
          sendMessage("", MESSAGE_TYPES?.LOCATION, value);
          setShowLocationPicker(false);
        }}
      />
      {/* 🔹 Header */}
      <div className="bg-white shadow p-3 flex items-center gap-2">
        {activeChatData?.type == CHAT_TYPES?.CARDS && (
          <div className="rounded-full bg-gray-200 p-3 w-fit">
            {activeChatData?.sportIcon}
          </div>
        )}
        {/* group profile */}
        {activeChatData?.type == CHAT_TYPES?.GROUPS && (
          <div
            style={
              activeChatData?.groupProfileImage
                ? {
                    backgroundImage: `url(${activeChatData?.groupProfileImage})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    backgroundRepeat: "no-repeat",
                  }
                : {
                    backgroundColor: "#2bace2",
                    color: "white",
                  }
            }
            className="rounded-full w-10 h-10 text-white font-semibold flex justify-center items-center"
          >
            {activeChatData?.groupProfileImage
              ? ""
              : activeChatData?.groupName &&
                activeChatData?.groupName?.charAt(0)?.toUpperCase()}
          </div>
        )}
        {/* connection profile */}
        {activeChatData?.type == CHAT_TYPES?.CONNECTIONS && (
          <div
            style={
              handleGetInfo(activeChatData, "profileImageUrl")
                ? {
                    backgroundImage: `url(${handleGetInfo(
                      activeChatData,
                      "profileImageUrl"
                    )})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    backgroundRepeat: "no-repeat",
                  }
                : {
                    background: "#2bace2",
                    color: "white",
                  }
            }
            className="rounded-full w-10 h-10 text-white font-semibold flex justify-center items-center"
          >
            {!handleGetInfo(activeChatData, "profileImageUrl") &&
              handleGetInfo(activeChatData, "name") &&
              handleGetInfo(activeChatData, "name")?.charAt(0)?.toUpperCase()}
          </div>
        )}
        <div className="relative w-full">
          <h2 className="font-semibold text-gray-800 truncate w-48">
            {activeChatData?.type == CHAT_TYPES?.GROUPS
              ? activeChatData?.groupName
              : activeChatData?.type == CHAT_TYPES?.CONNECTIONS
              ? handleGetInfo(activeChatData, "name")
              : activeChatData?.refID}
          </h2>
          <p className="text-sm text-gray-700 truncate w-48">
            {activeChatData?.sportName}
          </p>
          {activeChatData?.type == CHAT_TYPES?.GROUPS && (
            <div className="absolute top-0 right-3">
              <PiDotsThreeCircleLight
                onClick={(e) => {
                  e.stopPropagation();
                  setShowActions(!showActions);
                }}
                className="h-6 w-6"
              />
              <DropDown
                handleClick={(value) => {
                  if (value == "Delete") {
                    setDeleteDialog(true);
                  }
                  if (value == "View") {
                    setShowGroupDetails(true);
                  }
                  if (value == "Edit") {
                    handleOpenEditChat();
                  }
                  setShowActions(false);
                }}
                show={showActions}
                dropdownItems={
                  activeChatData?.groupCreaterId == CURRENT_USER_ID
                    ? ActionItems
                    : ["View"]
                }
              />
            </div>
          )}
        </div>
      </div>

      {/* 🔹 Messages (ONLY THIS SCROLLS) */}
      <div className="flex-1 overflow-y-auto lg:max-h-[64vh] p-4 space-y-3">
        {Object.entries(groupedMessages).map(([date, msgs]) => (
          <div key={date}>
            {moment(date)?.calendar(null, {
              sameDay: "[Today]",
              lastDay: "[Yesterday]",
              sameElse: "DD MMM YYYY",
            }) != "Invalid date" && (
              <div className="text-center bg-gray-800 rounded-lg px-2 py-1  w-fit mx-auto text-xs font-semibold text-gray-200 my-2">
                {moment(date).calendar(null, {
                  sameDay: "[Today]",
                  lastDay: "[Yesterday]",
                  lastWeek: "DD MMM YYYY",
                  nextWeek: "DD MMM YYYY",
                  sameElse: "DD MMM YYYY",
                })}
              </div>
            )}

            {msgs?.map((msg) => (
              <div
                key={msg.id}
                className={`flex mt-4 ${
                  msg.senderId === CURRENT_USER_ID
                    ? "justify-end"
                    : "items-start gap-2"
                }`}
              >
                {/* {msg.senderId != CURRENT_USER_ID && (
                  <img
                    src={src || defaultImg}
                    alt="avatar"
                    className="w-8 h-8 rounded-full object-cover"
                  />
                )} */}
                {msg?.messageType == MESSAGE_TYPES?.SYSTEM ? (
                  <div className="text-center bg-gray-600 rounded-lg px-2 py-1  w-fit mx-auto text-xs  text-gray-200 my-2">
                    {msg?.text}
                  </div>
                ) : (
                  <div>
                    <div
                      className={`py-2 px-3 rounded-lg shadow text-sm max-w-xs truncate ${
                        msg.senderId === CURRENT_USER_ID
                          ? "bg-[#2BACE2] text-white"
                          : "bg-white text-gray-800"
                      }`}
                    >
                      {msg.senderId != CURRENT_USER_ID && (
                        <p className="text-gray-300 text-xs font-semibold">
                          {activeChatData?.usersInfo &&
                            activeChatData?.usersInfo[msg.senderId]?.name}
                        </p>
                      )}
                      {msg.messageType === MESSAGE_TYPES?.LOCATION ? (
                        <div className="bg-gray-100 p-2 rounded">
                          <iframe
                            className="mt-2 mb-4 rounded"
                            width="250"
                            height="150"
                            loading="lazy"
                            src={`https://www.google.com/maps?q=${msg?.location?.lat},${msg?.location?.lng}&z=15&output=embed`}
                          />
                          <a
                            href={`https://www.google.com/maps?q=${msg?.location?.lat},${msg?.location?.lng}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 w-full text-wrap"
                          >
                            📍{" "}
                            {msg?.location?.address
                              ? msg?.location?.address
                              : "View Location"}
                          </a>
                        </div>
                      ) : msg.messageType === MESSAGE_TYPES?.IMAGE ? (
                        <div className="h-44 w-44">
                          <img src={msg?.text} alt="" width={"100%"} />
                        </div>
                      ) : (
                        msg.text
                      )}
                    </div>
                    <p
                      style={{
                        textAlign:
                          msg.senderId === CURRENT_USER_ID ? "end" : "start",
                      }}
                      className="text-xs px-2 pt-1 text-gray-400"
                    >
                      {getChatKey(msg?.createdAt)}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        ))}

        <div ref={bottomRef} />
      </div>

      {/* 🔹 Input */}
      {activeChatData?.isConnectionEnd ? (
        <div className="p-3 text-center  bg-white  border-t">
          This connection has ended. You can no longer send messages in this
          chat.
        </div>
      ) : (
        <div className="p-3 bg-white flex items-center gap-1 border-t">
          <input
            multiple
            type="file"
            accept="image/*"
            ref={fileInputRef}
            onChange={handleFileSelect}
            className="hidden"
          />

          <button
            onClick={handleAttachment}
            className="md:p-2 p-1 hover:bg-gray-200 rounded-full"
          >
            <FiPaperclip className="h-5 w-5" />
          </button>
          <button
            onClick={() => setShowLocationPicker(true)}
            className="md:p-2 p-1 hover:bg-gray-200 rounded-full"
          >
            <MdAddLocationAlt className="h-6 w-6" />
          </button>
          <input
            type="text"
            placeholder="Type a message..."
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && sendMessage()}
            className="flex-1 bg-gray-100 p-2 md:w-full w-24 rounded-lg focus:outline-none text-sm"
          />

          <button
            onClick={() => sendMessage()}
            className="bg-brand-gradient text-white md:px-4 px-2 py-2 rounded-lg flex items-center"
          >
            <FiSend />
          </button>
        </div>
      )}

      <ConfirmationDialog
        actionButtonName="Delete"
        onConfirm={handleDeleteGroup}
        closeDialog={() => {
          setDeleteDialog(false);
        }}
        buttonShow={["actionButton", "Cancel"]}
        isDialogOpen={{
          open: deleteDialog,
          message: "Are you sure you want to delete this group ?",
        }}
      />
      {/* group details dialog */}
      {showGroupDetails && (
        <GroupDetailsDialog
          onClose={() => setShowGroupDetails(false)}
          data={activeChatData}
          handleLeftGroup={handleLeftGroup}
          CURRENT_USER_ID={CURRENT_USER_ID}
        />
      )}
    </div>
  );
};

export default memo(ChatScreen);
